import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, MessageSquare } from "lucide-react";
import api from "../lib/api";

/**
 * Admin-only editor for the public Testimonials rail on the home page.
 * Fetches the current list and lets the admin add / edit / reorder / delete
 * quotes, then saves the whole batch in one atomic replace. When empty,
 * the public site falls back to its built-in starter quotes.
 */
const BLANK = { stars: 5, quote: "", name: "", event: "", tag: "Weddings", order: 0 };

const TestimonialsAdminPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/testimonials")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addRow = () => setItems((xs) => [...xs, { ...BLANK, order: xs.length }]);
  const removeRow = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));
  const patch = (i, k, v) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const move = (i, dir) => {
    setItems((xs) => {
      const j = i + dir;
      if (j < 0 || j >= xs.length) return xs;
      const copy = [...xs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy.map((x, idx) => ({ ...x, order: idx }));
    });
  };

  const save = async () => {
    setMsg("");
    for (const t of items) {
      if (!t.quote?.trim() || !t.name?.trim() || !t.event?.trim() || !t.tag?.trim()) {
        setMsg("Every testimonial needs quote, name, event, and tag.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { items: items.map((t, i) => ({ ...t, order: i })) };
      const r = await api.post("/testimonials", payload);
      setMsg(`Saved ${r.data?.count ?? items.length} testimonial(s).`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-testimonials-panel"
      className="bg-white rounded-2xl soft-shadow p-5 md:p-6 space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-coral-300" />
          <h3 className="font-semibold text-navy-700 text-base">Testimonials</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-testimonials-add"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 border border-navy-700/15 text-navy-700 text-sm px-3 py-1.5 rounded-full hover:bg-cream-100"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
          <button
            data-testid="admin-testimonials-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-coral-300 text-white text-sm px-4 py-1.5 rounded-full hover:bg-coral-400 disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save all"}
          </button>
        </div>
      </div>

      <p className="text-xs text-navy-700/60">
        Replaces the home page testimonials rail. Leave empty to show the built-in starter quotes.
      </p>

      {loading ? (
        <div className="text-sm text-navy-700/60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-navy-700/60 border border-dashed border-navy-700/15 rounded-xl p-6 text-center">
          No live testimonials yet. The site is showing the built-in starter quotes.
          Click <span className="font-semibold">Add</span> to publish real ones.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((t, i) => (
            <div
              key={i}
              data-testid={`admin-testimonial-row-${i}`}
              className="border border-navy-700/10 rounded-xl p-4 space-y-3 bg-cream-100/40"
            >
              <div className="grid md:grid-cols-4 gap-3">
                <input
                  value={t.name}
                  onChange={(e) => patch(i, "name", e.target.value)}
                  placeholder="Name (e.g. Priya & Marcus)"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid={`admin-testimonial-name-${i}`}
                />
                <input
                  value={t.event}
                  onChange={(e) => patch(i, "event", e.target.value)}
                  placeholder="Event · City"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid={`admin-testimonial-event-${i}`}
                />
                <input
                  value={t.tag}
                  onChange={(e) => patch(i, "tag", e.target.value)}
                  placeholder="Tag (Weddings / Birthdays / …)"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid={`admin-testimonial-tag-${i}`}
                />
                <select
                  value={t.stars}
                  onChange={(e) => patch(i, "stars", Number(e.target.value))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid={`admin-testimonial-stars-${i}`}
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={t.quote}
                onChange={(e) => patch(i, "quote", e.target.value)}
                placeholder="The guest's own words — 1–3 sentences work best."
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                data-testid={`admin-testimonial-quote-${i}`}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="text-xs px-2 py-1 border border-navy-700/15 rounded hover:bg-white disabled:opacity-40"
                  data-testid={`admin-testimonial-up-${i}`}
                >↑ Up</button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  className="text-xs px-2 py-1 border border-navy-700/15 rounded hover:bg-white disabled:opacity-40"
                  data-testid={`admin-testimonial-down-${i}`}
                >↓ Down</button>
                <button
                  onClick={() => removeRow(i)}
                  className="inline-flex items-center gap-1 text-xs text-red-600 px-2 py-1 border border-red-200 rounded hover:bg-red-50"
                  data-testid={`admin-testimonial-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div data-testid="admin-testimonials-msg" className="text-sm text-emerald-700">{msg}</div>
      )}
    </div>
  );
};

export default TestimonialsAdminPanel;
